import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Monitor, 
  CheckCircle, 
  X, 
  ArrowRight,
  Users,
  Clock,
  Shield,
  BarChart3,
  Bell,
  Globe,
  CreditCard,
  Smartphone
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly or yearly

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/subscription/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = {
    free: [
      { name: '1 website monitor', included: true },
      { name: '5-minute check intervals', included: true },
      { name: 'Email notifications', included: true },
      { name: 'Basic dashboard', included: true },
      { name: 'SMS notifications', included: false },
      { name: 'Advanced analytics', included: false },
      { name: 'API access', included: false },
      { name: 'Team features', included: false },
      { name: 'Priority support', included: false },
    ],
    pro: [
      { name: '10 website monitors', included: true },
      { name: '1-minute check intervals', included: true },
      { name: 'Email notifications', included: true },
      { name: 'SMS notifications', included: true },
      { name: 'Advanced analytics', included: true },
      { name: 'Incident history', included: true },
      { name: 'Custom alert rules', included: true },
      { name: 'API access', included: false },
      { name: 'Team features', included: false },
    ],
    business: [
      { name: '50 website monitors', included: true },
      { name: '30-second check intervals', included: true },
      { name: 'Email notifications', included: true },
      { name: 'SMS notifications', included: true },
      { name: 'Advanced analytics', included: true },
      { name: 'API access', included: true },
      { name: 'Team features', included: true },
      { name: 'Priority support', included: true },
      { name: 'Custom integrations', included: true },
    ]
  };

  return (
    <>
      <Helmet>
        <title>Pricing - URL Monitor Pro | Website Uptime Monitoring Plans</title>
        <meta name="description" content="Choose the perfect website monitoring plan for your business. Start free or upgrade to Pro/Business plans with advanced features and better monitoring intervals." />
        <meta name="keywords" content="website monitoring pricing, uptime monitoring plans, monitoring service cost, website tracker pricing" />
        <link rel="canonical" href="https://urlmonitor.pro/pricing" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center">
                <Monitor className="w-8 h-8 text-blue-600 mr-3" />
                <span className="text-xl font-bold text-gray-900">URL Monitor Pro</span>
              </Link>
              <div className="hidden md:flex items-center space-x-8">
                <Link to="/#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</Link>
                <Link to="/pricing" className="text-blue-600 font-semibold">Pricing</Link>
                <Link to="/about" className="text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors">Login</Link>
                <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  Start Free
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Header */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Simple, transparent pricing
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Start monitoring for free, then upgrade to unlock more monitors and advanced features. No hidden fees, cancel anytime.
            </p>
            
            {/* Billing Toggle */}
            <div className="flex items-center justify-center mb-12">
              <span className={`mr-3 ${billingCycle === 'monthly' ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`ml-3 ${billingCycle === 'yearly' ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
                Yearly
              </span>
              {billingCycle === 'yearly' && (
                <span className="ml-2 bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
                  Save 20%
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Pricing Plans */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              
              {/* Free Plan */}
              <div className="bg-white rounded-2xl border-2 border-gray-200 p-8 relative">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
                  <div className="text-5xl font-bold text-gray-900 mb-2">
                    $0
                  </div>
                  <p className="text-gray-600">Forever free</p>
                </div>
                
                <div className="space-y-4 mb-8">
                  {features.free.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      {feature.included ? (
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      ) : (
                        <X className="w-5 h-5 text-gray-300 mr-3 flex-shrink-0" />
                      )}
                      <span className={feature.included ? 'text-gray-900' : 'text-gray-400'}>
                        {feature.name}
                      </span>
                    </div>
                  ))}
                </div>
                
                <Link 
                  to="/register"
                  className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-800 transition-colors block text-center"
                >
                  Get Started Free
                </Link>
              </div>

              {/* Pro Plan */}
              <div className="bg-white rounded-2xl border-2 border-blue-500 p-8 relative transform scale-105 z-10">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Pro</h3>
                  <div className="text-5xl font-bold text-gray-900 mb-2">
                    ${billingCycle === 'monthly' ? (packages.pro?.price_usd || 19) : Math.round((packages.pro?.price_usd || 19) * 12 * 0.8)}
                  </div>
                  <p className="text-gray-600">
                    {billingCycle === 'monthly' ? 'per month' : 'per year'}
                  </p>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-green-600 font-medium mt-1">
                      Save ${Math.round((packages.pro?.price_usd || 19) * 12 * 0.2)} per year
                    </p>
                  )}
                </div>
                
                <div className="space-y-4 mb-8">
                  {features.pro.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      {feature.included ? (
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      ) : (
                        <X className="w-5 h-5 text-gray-300 mr-3 flex-shrink-0" />
                      )}
                      <span className={feature.included ? 'text-gray-900' : 'text-gray-400'}>
                        {feature.name}
                      </span>
                    </div>
                  ))}
                </div>
                
                <Link 
                  to="/register"
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors block text-center mb-4"
                >
                  Start Pro Trial
                </Link>
                
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <CreditCard className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                    <p className="text-xs text-blue-800">International Cards</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <Smartphone className="w-5 h-5 text-orange-600 mx-auto mb-1" />
                    <p className="text-xs text-orange-800">UPI & Indian Cards</p>
                  </div>
                </div>
              </div>

              {/* Business Plan */}
              <div className="bg-white rounded-2xl border-2 border-gray-200 p-8 relative">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Business</h3>
                  <div className="text-5xl font-bold text-gray-900 mb-2">
                    ${billingCycle === 'monthly' ? (packages.business?.price_usd || 49) : Math.round((packages.business?.price_usd || 49) * 12 * 0.8)}
                  </div>
                  <p className="text-gray-600">
                    {billingCycle === 'monthly' ? 'per month' : 'per year'}
                  </p>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-green-600 font-medium mt-1">
                      Save ${Math.round((packages.business?.price_usd || 49) * 12 * 0.2)} per year
                    </p>
                  )}
                </div>
                
                <div className="space-y-4 mb-8">
                  {features.business.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-900">{feature.name}</span>
                    </div>
                  ))}
                </div>
                
                <Link 
                  to="/register"
                  className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-800 transition-colors block text-center"
                >
                  Start Business Trial
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Indian Pricing */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Special pricing for India 🇮🇳
              </h2>
              <p className="text-xl text-gray-600">
                Pay in INR with UPI, cards, and net banking
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl border-2 border-orange-200 p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Pro Plan (India)</h3>
                  <div className="text-5xl font-bold text-gray-900 mb-2">
                    ₹{packages.pro?.price_inr || 1500}
                  </div>
                  <p className="text-gray-600">per month</p>
                </div>
                
                <div className="space-y-3 mb-8">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>UPI payments (Google Pay, PhonePe, Paytm)</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Indian debit/credit cards</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Net banking</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>eMandate for recurring payments</span>
                  </div>
                </div>
                
                <Link 
                  to="/register"
                  className="w-full bg-orange-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-700 transition-colors block text-center"
                >
                  Pay with UPI
                </Link>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border-2 border-purple-200 p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Business Plan (India)</h3>
                  <div className="text-5xl font-bold text-gray-900 mb-2">
                    ₹{packages.business?.price_inr || 3500}
                  </div>
                  <p className="text-gray-600">per month</p>
                </div>
                
                <div className="space-y-3 mb-8">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>All Indian payment methods</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Enterprise billing support</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>GST invoices</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Priority Indian timezone support</span>
                  </div>
                </div>
                
                <Link 
                  to="/register"
                  className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors block text-center"
                >
                  Pay with UPI
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently asked questions
              </h2>
            </div>

            <div className="space-y-8">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Can I start with the free plan?
                </h3>
                <p className="text-gray-600">
                  Yes! Our free plan allows you to monitor 1 website with 5-minute intervals and email alerts. Perfect for getting started and testing our service.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Do you offer refunds?
                </h3>
                <p className="text-gray-600">
                  We offer a 30-day money-back guarantee for all paid plans. If you're not satisfied, contact us for a full refund.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  What payment methods do you accept?
                </h3>
                <p className="text-gray-600">
                  We accept international credit/debit cards via Stripe. For Indian customers, we also support UPI (Google Pay, PhonePe, Paytm), Indian cards, net banking, and wallets via Razorpay.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Can I upgrade or downgrade anytime?
                </h3>
                <p className="text-gray-600">
                  Yes, you can change your plan anytime. Upgrades take effect immediately, and downgrades take effect at the end of your current billing cycle.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-blue-600 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to start monitoring?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of businesses who trust URL Monitor Pro to keep their websites online.
            </p>
            <Link 
              to="/register"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Free Monitoring
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <Link to="/" className="flex items-center justify-center mb-4">
                <Monitor className="w-8 h-8 text-blue-400 mr-3" />
                <span className="text-xl font-bold">URL Monitor Pro</span>
              </Link>
              <p className="text-gray-400">
                © 2024 URL Monitor Pro. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PricingPage;